import * as React from "react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type OptionType = {
  label: string;
  value: string;
};

interface MultiSelectProps {
  options: OptionType[];
  selected: string[];
  onChange: (values: string[]) => void;
  className?: string;
  placeholder?: string;
  emptyPlaceholder?: string;
  maxDisplayValues?: number;
  searchPlaceholder?: string;
  selectAllLabel?: string;
  showSelectAll?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  placeholder = "Select options",
  emptyPlaceholder = "No options found",
  maxDisplayValues = 2,
  searchPlaceholder = "Search options...",
  selectAllLabel = "Select all",
  showSelectAll = true,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Clear search query when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const handleUnselect = (option: string) => {
    onChange(selected.filter((selectedOption) => selectedOption !== option));
  };

  const handleSelectAll = () => {
    onChange(options.map((option) => option.value));
  };

  const handleUnselectAll = () => {
    onChange([]);
  };

  // Display selected values in the trigger button
  const displayValues = () => {
    // If nothing selected show placeholder
    if (selected.length === 0) {
      return <span className="text-neutral-500">{placeholder}</span>;
    }

    // Show badges for selected options up to maxDisplayValues
    return (
      <div className="flex gap-1 flex-wrap">
        {selected.slice(0, maxDisplayValues).map((value) => {
          const option = options.find((opt) => opt.value === value);
          return (
            <Badge
              key={value}
              variant="secondary"
              className="mr-1 mb-1 pl-2 pr-1 py-1 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20"
              onClick={(e) => {
                e.stopPropagation();
                handleUnselect(value);
              }}
            >
              {option?.label || value}
              <button 
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnselect(value);
                }}
              >
                <XIcon className="h-3 w-3 cursor-pointer" />
              </button>
            </Badge>
          );
        })}
        {selected.length > maxDisplayValues && (
          <Badge 
            variant="secondary" 
            className="mb-1 px-2 py-1 bg-primary/10 text-primary border border-primary/20"
          >
            +{selected.length - maxDisplayValues} more
          </Badge>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("min-h-10 h-auto py-2 justify-between", className)}
        >
          <div className="flex flex-wrap truncate">
            {displayValues()}
          </div>
          <div className={selected.length > 0 ? "ml-2" : "ml-auto"}>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="w-full border-b border-neutral-100">
          <input
            className="w-full px-3 py-2 outline-none text-sm"
            placeholder={searchPlaceholder}
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
          />
        </div>
        
        <Command className={cn("w-full hidden")}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandEmpty>{emptyPlaceholder}</CommandEmpty>
        </Command>
        
        <div className="w-full">
          {showSelectAll && (
            <div className="flex justify-between px-2 py-2 border-b border-neutral-100 bg-neutral-50">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-primary hover:text-primary/90 hover:bg-primary/10 font-medium"
                onClick={handleSelectAll}
              >
                <CheckIcon className="mr-1 h-3 w-3" /> {selectAllLabel}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-neutral-600 hover:text-neutral-800 hover:bg-neutral-200"
                onClick={handleUnselectAll}
              >
                <XIcon className="mr-1 h-3 w-3" /> Clear
              </Button>
            </div>
          )}
          
          <div className="max-h-64 overflow-auto">
            {options
              .filter(option => 
                searchQuery.trim() === "" || 
                option.label.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className="px-2 py-2 flex items-center cursor-pointer hover:bg-neutral-100 border-b border-neutral-100 last:border-b-0"
                    onClick={() => {
                      onChange(
                        isSelected
                          ? selected.filter((selectedOption) => selectedOption !== option.value)
                          : [...selected, option.value]
                      );
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-5 w-5 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50"
                      )}
                    >
                      <CheckIcon className={cn("h-3 w-3", !isSelected && "invisible")} />
                    </div>
                    <span className="flex-1 cursor-pointer text-sm">
                      {option.label}
                    </span>
                  </div>
                );
              })}
              
            {options.filter(option => 
              searchQuery.trim() !== "" && 
              option.label.toLowerCase().includes(searchQuery.toLowerCase())
            ).length === 0 && searchQuery.trim() !== "" && (
              <div className="p-2 text-center text-sm text-neutral-500">
                {emptyPlaceholder}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Simple chevron down icon component to avoid Lucide-React dependency
function ChevronDown(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}